<template>
  <div class="port-root">

    <!-- Loading state -->
    <template v-if="store.loading || customerLoading">
      <div class="state-wrap">
        <div class="nd-icon">⏳</div>
        <h2>กำลังโหลดข้อมูล…</h2>
      </div>
    </template>

    <!-- Not found -->
    <template v-else-if="!customer">
      <div class="state-wrap">
        <div class="nd-icon">🔍</div>
        <h2>ไม่พบข้อมูลลูกค้า</h2>
        <p>ไม่พบ "{{ decodedId }}" ในผลการวิเคราะห์</p>
        <button class="btn-back" @click="router.push({ name: 'CsDashboard' })">← กลับ Dashboard</button>
      </div>
    </template>

    <template v-else>
      <!-- Page header -->
      <div class="page-hdr">
        <button class="btn-hdr-ghost" @click="router.go(-1)">← กลับ</button>
        <div class="page-hdr-info">
          <div class="page-hdr-name">
            {{ customer.customerName }}
            <span :class="'tier-hdr tier-hdr-' + customer.tierKey">{{ customer.tier }}</span>
          </div>
          <div class="page-hdr-sub">ข้อมูล ณ วันที่ {{ analysisDate }}</div>
        </div>
        <button class="btn-hdr-solid" @click="store.selectedCustomerId = customerRouteId(customer); router.push({ name: 'CsCustomerDetail' })">รายละเอียดคะแนน →</button>
      </div>

      <div class="port-main">

        <!-- Alerts -->
        <div v-if="customer.overdueChains > 0" class="alert-banner alert-red">
          <span class="alert-icon">🚨</span>
          <span class="alert-text">
            <strong>ค้างชำระ {{ customer.overdueChains }} ตั๋ว</strong> — เงินต้น {{ fmtBaht(customer.overduePrincipal) }} · สูงสุด {{ customer.maxDaysOverdue }} วัน · ควรติดต่อลูกค้าโดยด่วน
          </span>
        </div>
        <div v-if="customer.earlyWarningAdj <= -10" class="alert-banner alert-amber">
          <span class="alert-icon">⚠️</span>
          <span class="alert-text"><strong>Early Warning:</strong> สัดส่วนตั๋วค้างชำระต่อเงินต้นเปิดสูง — ต้องติดตามอย่างใกล้ชิด</span>
        </div>
        <div v-else-if="customer.earlyWarningAdj < 0" class="alert-banner alert-amber">
          <span class="alert-icon">⚠️</span>
          <span class="alert-text"><strong>Early Warning:</strong> เริ่มมีตั๋วที่ใกล้/เกินกำหนด — ควรติดตาม</span>
        </div>
        <div v-if="forfeitPct >= 30" class="alert-banner alert-amber">
          <span class="alert-icon">📋</span>
          <span class="alert-text"><strong>ประวัติหลุด:</strong> {{ customer.forfeitedChains }} ตั๋ว คิดเป็น {{ Math.round(forfeitPct) }}% ของตั๋วสะสมทั้งหมด</span>
        </div>

        <!-- Score summary -->
        <div class="section">
          <div class="cs-card" style="padding:0;overflow:hidden;">
            <div class="score-summary">
              <div class="ss-tier">
                <span :class="'tier-badge tier-' + customer.tierKey">{{ customer.tier }}</span>
                <div class="ss-tier-name">{{ tierDesc(customer.tierKey) }}</div>
              </div>
              <div class="ss-score">
                <div class="ss-score-lbl">ความน่าเชื่อถือ (Risk)</div>
                <div class="ss-score-val ss-risk-val">{{ fmtNum(customer.riskFinal, 2) }}</div>
                <div class="ss-bar-bg"><div class="ss-bar-fill ss-risk-fill" :style="{ width: customer.riskFinal + '%' }"></div></div>
                <div class="ss-band"><span :class="bandCls(customer.riskFinal)">{{ bandLabelFull(customer.riskFinal) }}</span></div>
              </div>
              <div class="ss-score">
                <div class="ss-score-lbl">มูลค่า (Value)</div>
                <div class="ss-score-val ss-value-val">{{ fmtNum(customer.valueFinal, 2) }}</div>
                <div class="ss-bar-bg"><div class="ss-bar-fill ss-value-fill" :style="{ width: customer.valueFinal + '%' }"></div></div>
                <div class="ss-band"><span :class="bandCls(customer.valueFinal)">{{ bandLabelFull(customer.valueFinal) }}</span></div>
              </div>
              <div class="ss-score" style="border-right:none">
                <div class="ss-score-lbl">ดัชนีข้อมูล (DMI)</div>
                <div class="ss-score-val ss-dmi-val">{{ fmtNum(customer.dmi, 2) }}</div>
                <div class="ss-bar-bg"><div class="ss-bar-fill ss-dmi-fill" :style="{ width: Math.min(customer.dmi, 100) + '%' }"></div></div>
                <div class="ss-band"><span :class="dmiCls(customer.dmi)">{{ dmiLabel(customer.dmi) }}</span></div>
              </div>
            </div>
          </div>
        </div>

        <!-- Main 2-column -->
        <div class="section main-grid">

          <!-- Left: Ticket portfolio -->
          <div class="cs-card">
            <div class="cs-card-title">Portfolio ตั๋วจำนำ</div>
            <div class="ticket-grid">
              <div class="t-stat">
                <div class="t-stat-lbl">ตั๋วเปิดอยู่</div>
                <div class="t-stat-val ts-open">{{ customer.openChains }}</div>
                <div class="t-stat-sub">เงินต้น {{ fmtBaht(customer.openPrincipal) }}</div>
                <div v-if="phlatCount > 0" class="t-stat-phlat">ผลัด {{ phlatCount }} ตั๋ว · {{ fmtBaht(phlatPrincipal) }}</div>
              </div>
              <div class="t-stat">
                <div class="t-stat-lbl">ตั๋วค้างชำระ</div>
                <div class="t-stat-val" :class="customer.overdueChains > 0 ? 'ts-overdue' : 'ts-ok'">{{ customer.overdueChains }}</div>
                <div class="t-stat-sub" :style="customer.overdueChains > 0 ? 'color:var(--red)' : ''">
                  {{ customer.overdueChains > 0
                    ? (fmtBaht(customer.overduePrincipal) + ' · ' + customer.maxDaysOverdue + ' วัน')
                    : 'ไม่มีค้างชำระ' }}
                </div>
              </div>
              <div class="t-stat">
                <div class="t-stat-lbl">ตั๋วปิดแล้ว</div>
                <div class="t-stat-val ts-closed">{{ customer.redeemedChains }}</div>
                <div class="t-stat-sub">{{ totalTickets > 0 ? Math.round(customer.redeemedChains / totalTickets * 100) + '% ของสะสม' : '—' }}</div>
                <div v-if="customer.thirdPartyRedeemedChains > 0" class="t-stat-breakdown">
                  <span>— ไถ่เอง {{ customer.redeemedChains - customer.thirdPartyRedeemedChains }}</span>
                  <span>— บุคคลอื่นไถ่แทน {{ customer.thirdPartyRedeemedChains }}</span>
                </div>
              </div>
              <div class="t-stat">
                <div class="t-stat-lbl">เคยหลุด</div>
                <div class="t-stat-val" :class="customer.forfeitedChains > 0 ? 'ts-forfeit' : 'ts-ok'">{{ customer.forfeitedChains }}</div>
                <div class="t-stat-sub">
                  {{ customer.forfeitedChains > 0
                    ? `เงินต้นรวม ${fmtBaht(forfeitNormalPrincipal + forfeitAfterRenewPrincipal)}`
                    : (totalTickets > 0 ? Math.round(customer.forfeitedChains / totalTickets * 100) + '% ของสะสม' : '—') }}
                </div>
              </div>
              <div v-if="customer.transferredChains > 0" class="t-stat">
                <div class="t-stat-lbl">เปลี่ยนชื่อตั๋ว</div>
                <div class="t-stat-val ts-transfer">{{ customer.transferredChains }}</div>
                <div class="t-stat-sub">{{ totalTickets > 0 ? Math.round(customer.transferredChains / totalTickets * 100) + '% ของสะสม' : '—' }}</div>
                <div class="t-stat-breakdown">
                  <span>— chain โอนให้ลูกค้าอื่น</span>
                </div>
              </div>
            </div>
            <div v-if="customer.overdueChains > 0" class="overdue-box">
              <div class="overdue-box-lbl">รายละเอียดค้างชำระ</div>
              <div class="od-row">
                <span class="od-key">จำนวนตั๋วที่ค้างชำระ</span>
                <span class="od-badge">{{ customer.overdueChains }} ตั๋ว</span>
              </div>
              <div class="od-row">
                <span class="od-key">เงินต้นค้างชำระรวม</span>
                <span class="od-val">{{ fmtBaht(customer.overduePrincipal) }}</span>
              </div>
              <div class="od-row">
                <span class="od-key">ค้างชำระสูงสุด</span>
                <span class="od-val">{{ customer.maxDaysOverdue }} วัน</span>
              </div>
            </div>
            <div v-if="customer.forfeitedChains > 0" class="forfeit-box">
              <div class="forfeit-box-lbl">รายละเอียดตั๋วหลุด</div>
              <div class="fod-row">
                <span class="fod-key">หลุดปกติ <span class="fod-principal">({{ fmtBahtFull(forfeitNormalPrincipal) }})</span></span>
                <span class="fod-badge">{{ forfeitNormal }} ตั๋ว</span>
              </div>
              <template v-if="forfeitNormal > 0">
                <div class="fod-divider"></div>
                <div v-if="customer.forfeitedNormalByPawn > 0" class="fod-sub-row">
                  <span class="fod-sub-key">— จำนำแล้วหลุด</span>
                  <span class="fod-sub-val">{{ customer.forfeitedNormalByPawn }} ตั๋ว</span>
                </div>
                <div v-if="customer.forfeitedNormalByRenew > 0" class="fod-sub-row">
                  <span class="fod-sub-key">— ต่อดอกแล้วหลุด</span>
                  <span class="fod-sub-val">{{ customer.forfeitedNormalByRenew }} ตั๋ว</span>
                </div>
                <div v-if="customer.forfeitedNormalByReduce > 0" class="fod-sub-row">
                  <span class="fod-sub-key">— ลดต้นแล้วหลุด</span>
                  <span class="fod-sub-val">{{ customer.forfeitedNormalByReduce }} ตั๋ว</span>
                </div>
                <div v-if="customer.forfeitedNormalByIncrease > 0" class="fod-sub-row">
                  <span class="fod-sub-key">— เพิ่มต้นแล้วหลุด</span>
                  <span class="fod-sub-val">{{ customer.forfeitedNormalByIncrease }} ตั๋ว</span>
                </div>
              </template>
              <div class="fod-section-sep"></div>
              <div class="fod-row">
                <span class="fod-key">ผลัดมาแล้วหลุด <span class="fod-principal">({{ fmtBahtFull(forfeitAfterRenewPrincipal) }})</span></span>
                <span class="fod-badge">{{ forfeitAfterRenew }} ตั๋ว</span>
              </div>
              <template v-if="forfeitAfterRenew > 0">
                <div class="fod-divider"></div>
                <div v-if="customer.forfeitedPhlatByPawn > 0" class="fod-sub-row">
                  <span class="fod-sub-key">— จำนำแล้วหลุด</span>
                  <span class="fod-sub-val">{{ customer.forfeitedPhlatByPawn }} ตั๋ว</span>
                </div>
                <div v-if="customer.forfeitedPhlatByRenew > 0" class="fod-sub-row">
                  <span class="fod-sub-key">— ต่อดอกแล้วหลุด</span>
                  <span class="fod-sub-val">{{ customer.forfeitedPhlatByRenew }} ตั๋ว</span>
                </div>
                <div v-if="customer.forfeitedPhlatByReduce > 0" class="fod-sub-row">
                  <span class="fod-sub-key">— ลดต้นแล้วหลุด</span>
                  <span class="fod-sub-val">{{ customer.forfeitedPhlatByReduce }} ตั๋ว</span>
                </div>
                <div v-if="customer.forfeitedPhlatByIncrease > 0" class="fod-sub-row">
                  <span class="fod-sub-key">— เพิ่มต้นแล้วหลุด</span>
                  <span class="fod-sub-val">{{ customer.forfeitedPhlatByIncrease }} ตั๋ว</span>
                </div>
              </template>
            </div>
          </div>

          <!-- Right: Financial summary -->
          <div class="cs-card">
            <div class="cs-card-title">สรุปการเงิน</div>
            <div class="p-row">
              <span class="p-label">เงินต้นเปิดอยู่</span>
              <div class="p-bar-wrap">
                <div class="p-bar-bg"><div class="p-bar-fill p-open-fill" style="width:100%"></div></div>
              </div>
              <span class="p-amount">{{ fmtBaht(customer.openPrincipal) }}</span>
            </div>
            <div v-if="customer.overdueChains > 0" class="p-row">
              <span class="p-label" style="color:var(--red)">ค้างชำระ</span>
              <div class="p-bar-wrap">
                <div class="p-bar-bg">
                  <div
class="p-bar-fill p-overdue-fill"
                    :style="{ width: Math.min(customer.overduePrincipal / Math.max(customer.openPrincipal, 1) * 100, 100) + '%' }">
                  </div>
                </div>
              </div>
              <span class="p-amount" style="color:var(--red)">{{ fmtBaht(customer.overduePrincipal) }}</span>
            </div>
            <div class="divider"></div>
            <div class="fin-grid">
              <div>
                <div class="fin-lbl">ธุรกรรมทั้งหมด</div>
                <div class="fin-val">{{ customer.totalTransactions }} รายการ</div>
              </div>
              <div>
                <div class="fin-lbl">ตั๋วสะสมทั้งหมด</div>
                <div class="fin-val">{{ totalTickets }} ตั๋ว</div>
              </div>
              <div>
                <div class="fin-lbl">เงินต้นเปิดรวม</div>
                <div class="fin-val">{{ fmtBaht(customer.openPrincipal) }}</div>
              </div>
              <div>
                <div class="fin-lbl">อัตราหลุด</div>
                <div class="fin-val" :style="customer.forfeitedChains > 0 ? 'color:var(--amber)' : ''">
                  {{ totalTickets > 0 ? Math.round(customer.forfeitedChains / totalTickets * 100) + '%' : '—' }}
                </div>
              </div>
            </div>
            <div class="divider"></div>
            <div class="interest-breakdown">
              <div class="ib-header">
                <span class="ib-lbl">ดอกเบี้ยรับรวมทั้งหมด</span>
                <span class="ib-total">{{ fmtBahtFull(customer.totalInterest) }}</span>
              </div>
              <div class="ib-row">
                <span class="ib-dot ib-dot-closed"></span>
                <span class="ib-sub-lbl">จาก Chain ที่ปิดแล้ว</span>
                <div class="ib-bar-wrap">
                  <div class="ib-bar-bg">
                    <div
class="ib-bar-fill ib-closed-fill"
                      :style="{ width: customer.totalInterest > 0 ? Math.round(customer.interestFromClosed / customer.totalInterest * 100) + '%' : '0%' }">
                    </div>
                  </div>
                </div>
                <span class="ib-pct">{{ customer.totalInterest > 0 ? Math.round(customer.interestFromClosed / customer.totalInterest * 100) + '%' : '—' }}</span>
                <span class="ib-amt">{{ fmtBahtFull(customer.interestFromClosed) }}</span>
              </div>
              <div v-if="customer.interestFromTransferred > 0" class="ib-sub-note">
                ในนั้น {{ fmtBahtFull(customer.interestFromTransferred) }} จาก chain โอนออก
                (ไถ่/หลุด {{ fmtBahtFull(customer.interestFromClosed - customer.interestFromTransferred) }})
              </div>
              <div class="ib-row">
                <span class="ib-dot ib-dot-open"></span>
                <span class="ib-sub-lbl">จาก Chain ที่เปิดอยู่</span>
                <div class="ib-bar-wrap">
                  <div class="ib-bar-bg">
                    <div
class="ib-bar-fill ib-open-fill"
                      :style="{ width: customer.totalInterest > 0 ? Math.round(customer.interestFromOpen / customer.totalInterest * 100) + '%' : '0%' }">
                    </div>
                  </div>
                </div>
                <span class="ib-pct">{{ customer.totalInterest > 0 ? Math.round(customer.interestFromOpen / customer.totalInterest * 100) + '%' : '—' }}</span>
                <span class="ib-amt">{{ fmtBahtFull(customer.interestFromOpen) }}</span>
              </div>
            </div>
            <div class="divider"></div>
            <!-- การฝาก-ถอนเงิน — placeholder รอเชื่อม API -->
            <div class="ib-header" style="margin-bottom:10px">
              <span class="ib-lbl">การฝาก-ถอนเงิน</span>
              <span class="wallet-badge">ยอดเงินฝากคงเหลือ {{ fmtBahtFull(walletBalance) }}</span>
            </div>
            <div class="fin-grid">
              <div>
                <div class="fin-lbl">ฝากทั้งหมด</div>
                <div class="fin-val wallet-deposit">{{ depositCount }} ครั้ง</div>
              </div>
              <div>
                <div class="fin-lbl">ยอดฝากรวม</div>
                <div class="fin-val wallet-deposit">{{ fmtBahtFull(depositTotal) }}</div>
              </div>
              <div>
                <div class="fin-lbl">ถอนทั้งหมด</div>
                <div class="fin-val wallet-withdraw">{{ withdrawCount }} ครั้ง</div>
              </div>
              <div>
                <div class="fin-lbl">ยอดถอนรวม</div>
                <div class="fin-val wallet-withdraw">{{ fmtBahtFull(withdrawTotal) }}</div>
              </div>
            </div>
          </div>
        </div>

        <!-- Closed chain type breakdown -->
        <div v-if="customer.redeemedChains > 0 && closedTicketBreakdown.length > 0" class="section">
          <div class="cs-card">
            <div class="tb-header" @click="closedExpanded = !closedExpanded">
              <div class="tb-header-left">
                <span class="cs-card-title" style="margin-bottom:0">โครงสร้างตั๋วปิด — ธุรกรรมล่าสุดก่อนปิด</span>
                <span class="tb-total-badge">{{ customer.redeemedChains }} ตั๋ว</span>
              </div>
              <span class="tb-chevron" :class="{ 'tb-chevron-open': closedExpanded }">▾</span>
            </div>
            <div v-if="closedExpanded" class="tb-grid tb-grid-animated">
              <div v-for="tb in closedTicketBreakdown" :key="tb.key" class="tb-row">
                <div class="tb-label-wrap">
                  <span class="tb-dot" :class="'tb-dot-' + tb.key"></span>
                  <span class="tb-lbl">{{ tb.label }}</span>
                </div>
                <div class="tb-bar-wrap">
                  <div class="tb-bar-bg">
                    <div
class="tb-bar-fill" :class="'tb-fill-' + tb.key"
                      :style="{ width: customer.redeemedChains > 0 ? Math.round(tb.count / customer.redeemedChains * 100) + '%' : '0%' }">
                    </div>
                  </div>
                </div>
                <div class="tb-cnt">{{ tb.count }} ตั๋ว</div>
                <div class="tb-pct">{{ customer.redeemedChains > 0 ? Math.round(tb.count / customer.redeemedChains * 100) + '%' : '—' }}</div>
                <div class="tb-amt">{{ fmtBahtFull(tb.principal) }}</div>
              </div>
            </div>
          </div>
        </div>

        <!-- Open chain type breakdown -->
        <div v-if="customer.openChains > 0 && ticketBreakdown.length > 0" class="section">
          <div class="cs-card">
            <div class="tb-header tb-header-static">
              <div class="tb-header-left">
                <span class="cs-card-title" style="margin-bottom:0">โครงสร้างตั๋วเปิด — ธุรกรรมล่าสุดต่อตั๋ว</span>
                <span class="tb-total-badge tb-total-badge-open">{{ customer.openChains }} ตั๋ว</span>
              </div>
            </div>
            <div class="tb-grid">
              <div v-for="tb in ticketBreakdown" :key="tb.key" class="tb-row">
                <div class="tb-label-wrap">
                  <span class="tb-dot" :class="'tb-dot-' + tb.key"></span>
                  <span class="tb-lbl">{{ tb.label }}</span>
                </div>
                <div class="tb-bar-wrap">
                  <div class="tb-bar-bg">
                    <div
class="tb-bar-fill" :class="'tb-fill-' + tb.key"
                      :style="{ width: customer.openChains > 0 ? Math.round(tb.count / customer.openChains * 100) + '%' : '0%' }">
                    </div>
                  </div>
                </div>
                <div class="tb-cnt">{{ tb.count }} ตั๋ว</div>
                <div class="tb-pct">{{ customer.openChains > 0 ? Math.round(tb.count / customer.openChains * 100) + '%' : '—' }}</div>
                <div class="tb-amt">{{ fmtBahtFull(tb.principal) }}</div>
              </div>
            </div>
          </div>
        </div>

        <!-- Open tickets forfeit risk -->
        <div v-if="customer.openTickets && customer.openTickets.length > 0" class="section">
          <div class="cs-card">
            <div class="cs-card-title">ตั๋วเปิดอยู่ — ความเสี่ยงหลุด ({{ sortedOpenTickets.length }}{{ sortedOpenTickets.length !== customer.openTickets.length ? ' / ' + customer.openTickets.length : '' }} ตั๋ว)</div>
            <div class="ot-filters">
              <select v-model="filterAssetType" class="ot-filter-select">
                <option value="">ประเภททั้งหมด</option>
                <option v-for="opt in openTicketAssetOptions" :key="opt" :value="opt">{{ opt }}</option>
              </select>
              <select v-model="filterLastTxnType" class="ot-filter-select">
                <option value="">ธุรกรรมทั้งหมด</option>
                <option v-for="opt in openTicketTxnOptions" :key="opt" :value="opt">{{ txnLabel(opt) }}</option>
              </select>
              <select v-model="filterRiskLevel" class="ot-filter-select">
                <option value="">ระดับเสี่ยงทั้งหมด</option>
                <option value="high">สูง</option>
                <option value="mid">ปานกลาง</option>
                <option value="low">ต่ำ</option>
              </select>
              <select v-model="filterPhlat" class="ot-filter-select">
                <option value="">ตั๋วทั้งหมด</option>
                <option value="yes">ตั๋วที่ผลัด</option>
                <option value="no">ตั๋วที่ไม่ผลัด</option>
              </select>
              <button v-if="filterAssetType || filterLastTxnType || filterRiskLevel || filterPhlat" class="ot-filter-clear" @click="filterAssetType = ''; filterLastTxnType = ''; filterRiskLevel = ''; filterPhlat = ''">ล้าง</button>
            </div>
            <div class="ot-table-wrap">
              <table class="ot-table">
                <thead>
                  <tr>
                    <th>ครบกำหนด</th>
                    <th>หมายเลขตั๋ว</th>
                    <th class="col-stock">เลขสต็อก</th>
                    <th class="ot-c col-asset">ประเภท</th>
                    <th class="ot-r">วันที่เหลือ / เกิน</th>
                    <th class="ot-r">เงินต้น</th>
                    <th class="ot-c">ธุรกรรมล่าสุด</th>
                    <th class="ot-c">ระดับเสี่ยง</th>
                    <th class="ot-r">
                      % โอกาสหลุด
                      <button class="prob-info-btn" title="คำอธิบาย % โอกาสหลุด" @click.stop="showProbModal = true">ℹ</button>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(t, i) in sortedOpenTickets" :key="i" :class="ticketRowClass(t)">
                    <td class="ot-due">{{ fmtDate(t.dueDate) }}</td>
                    <td class="ot-ticket">{{ t.ticketNo || '—' }}</td>
                    <td class="col-stock">
                      <span v-if="t.stockNo" class="stock-no">{{ t.stockNo }}</span>
                      <span v-else class="ot-muted">—</span>
                    </td>
                    <td class="ot-c col-asset">
                      <span v-if="t.assetType" class="asset-chip">{{ t.assetType }}</span>
                      <span v-else class="ot-muted">—</span>
                    </td>
                    <td class="ot-r">
                      <span :class="daysClass(t.daysUntilDue)">{{ fmtDays(t.daysUntilDue) }}</span>
                      <span v-if="t.phlatStatus === 'IN_EXTENSION_WINDOW'" class="phlat-badge phlat-ok" title="ผลัดแล้ว — ยังอยู่ในช่วงวันที่สัญญา">ผลัด</span>
                      <span v-else-if="t.phlatStatus === 'PAST_PHLAT_UNTIL'" class="phlat-badge phlat-late" title="ผลัดแล้ว — เลยวันที่สัญญาแล้ว">เกินวันผลัด</span>
                    </td>
                    <td class="ot-r ot-principal">{{ fmtBahtFull(t.principal) }}</td>
                    <td class="ot-c">
                      <span :class="'ot-txn ot-txn-' + t.lastTxnType.toLowerCase()">{{ txnLabel(t.lastTxnType) }}</span>
                    </td>
                    <td class="ot-c">
                      <span :class="'ot-risk ot-risk-' + ticketRiskLevel(t)">{{ ticketRiskLabel(t) }}</span>
                    </td>
                    <td class="ot-r">
                      <template v-if="t.forfeitProbabilityPct != null">
                        <div class="prob-wrap">
                          <div class="prob-bar-bg">
                            <div class="prob-bar-fill" :style="ticketProbBarStyle(t)"></div>
                          </div>
                          <span :class="['prob-pct', ticketProbClass(t)]">
                            {{ Number(t.forfeitProbabilityPct).toFixed(1) }}%
                          </span>
                        </div>
                      </template>
                      <span v-else class="ot-muted">—</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <!-- Signals -->
        <div class="section">
          <div class="section-lbl">สัญญาณความเสี่ยง (Trend Adjustments)</div>
          <div class="signals-grid">
            <div v-for="sig in signals" :key="sig.lbl" class="signal-card">
              <div class="signal-lbl">{{ sig.lbl }}</div>
              <div class="signal-val" :class="sigCls(sig.val)">{{ sig.val >= 0 ? '+' : '' }}{{ fmtNum(sig.val, 1) }}</div>
              <div class="signal-desc" :class="sigCls(sig.val)">{{ sig.desc }}</div>
            </div>
          </div>
        </div>

        <!-- CTA -->
        <div class="section">
          <div class="detail-cta">
            <div class="cta-left">
              <h3>ต้องการดูรายละเอียดองค์ประกอบคะแนน?</h3>
              <p>ดูการแยกย่อย Risk / Value แต่ละ component, DMI anchoring, และ trend adjustments ทีละขั้น</p>
            </div>
            <button class="cta-btn" @click="store.selectedCustomerId = customerRouteId(customer); router.push({ name: 'CsCustomerDetail' })">ดูรายละเอียดคะแนน →</button>
          </div>
        </div>

      </div>
    </template>
  </div>

  <!-- Forfeit Probability Explanation Modal -->
  <Teleport to="body">
    <div v-if="showProbModal" class="prob-modal-overlay" @click.self="showProbModal = false">
      <div class="prob-modal">
        <div class="prob-modal-hdr">
          <div class="prob-modal-title">% โอกาสหลุด — คืออะไร?</div>
          <button class="prob-modal-close" @click="showProbModal = false">✕</button>
        </div>
        <div class="prob-modal-body">
          <p class="prob-modal-lead">
            ค่าที่แสดงคือ <strong>ความน่าจะเป็นที่ตั๋วใบนี้จะ "หลุด" (ทรัพย์หลุดจำนำ)</strong> โดยไม่ได้ถูกไถ่ถอน
            คำนวณจาก 4 ปัจจัยของลูกค้าและสถานะตั๋ว รวมเป็น <em>คะแนนรวม 0–100%</em>
          </p>

          <div class="prob-modal-formula">
            <span class="prob-formula-label">สูตร:</span>
            <code>P(หลุด) = P1×40% + P2×35% + P3×15% + P4×10%</code>
          </div>

          <div class="prob-modal-factors">
            <div class="pmf-row">
              <div class="pmf-badge pmf-p1">P1</div>
              <div class="pmf-content">
                <div class="pmf-name">ประวัติหลุด <span class="pmf-weight">น้ำหนัก 40%</span></div>
                <div class="pmf-desc">อัตราหลุดในอดีตของลูกค้า — คำนวณจากสัดส่วนตั๋วที่เคยหลุดต่อตั๋วที่ปิดแล้วทั้งหมด (×2, สูงสุด 100%) หากไม่มีประวัติ default = 30%</div>
              </div>
            </div>
            <div class="pmf-row">
              <div class="pmf-badge pmf-p2">P2</div>
              <div class="pmf-content">
                <div class="pmf-name">Risk Score ลูกค้า <span class="pmf-weight">น้ำหนัก 35%</span></div>
                <div class="pmf-desc">ความเสี่ยงรวมของลูกค้า — Risk Score ต่ำ = ความเสี่ยงสูง (P2 = 1 − riskFinal/100)</div>
              </div>
            </div>
            <div class="pmf-row">
              <div class="pmf-badge pmf-p3">P3</div>
              <div class="pmf-content">
                <div class="pmf-name">ค้างชำระปัจจุบัน <span class="pmf-weight">น้ำหนัก 15%</span></div>
                <div class="pmf-desc">จำนวนวันที่เกินกำหนดมากที่สุดในบรรดาตั๋วเปิดอยู่ทั้งหมด — 60 วันขึ้นไป = สูงสุด (ถ้าไม่มีตั๋วค้างชำระ = 0)</div>
              </div>
            </div>
            <div class="pmf-row" style="border-bottom:none">
              <div class="pmf-badge pmf-p4">P4</div>
              <div class="pmf-content">
                <div class="pmf-name">ความเร่งด่วนของตั๋วใบนี้ <span class="pmf-weight">น้ำหนัก 10%</span></div>
                <div class="pmf-desc">วันที่เหลือก่อนครบกำหนดของตั๋วใบนี้โดยเฉพาะ — ครบกำหนดวันนี้ = 100%, ยังเหลือ 20 วันขึ้นไป = 0%</div>
              </div>
            </div>
          </div>

          <div class="prob-modal-levels">
            <div class="pml-title">เกณฑ์ระดับความเสี่ยง</div>
            <div class="pml-row">
              <span class="pml-dot pml-high"></span>
              <span class="pml-label">สูง (HIGH)</span>
              <span class="pml-range">≥ 65%</span>
            </div>
            <div class="pml-row">
              <span class="pml-dot pml-mid"></span>
              <span class="pml-label">ปานกลาง (MEDIUM)</span>
              <span class="pml-range">35% – 64%</span>
            </div>
            <div class="pml-row">
              <span class="pml-dot pml-low"></span>
              <span class="pml-label">ต่ำ (LOW)</span>
              <span class="pml-range">&lt; 35%</span>
            </div>
          </div>

          <p class="prob-modal-note">
            หมายเหตุ: P1–P3 ใช้ข้อมูลระดับลูกค้าเหมือนกันทุกแถว · P4 เปลี่ยนตามวันครบกำหนดของแต่ละตั๋ว
          </p>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import { ref, computed, onMounted, watchEffect } from 'vue'
import { useRouter } from 'vue-router'
import { useScoreStore } from '@/stores/score.js'

const router = useRouter()
const store = useScoreStore()

onMounted(() => { store.init() })

const customer        = ref(null)
const customerLoading = ref(false)
const showProbModal      = ref(false)
const filterAssetType    = ref('')
const filterLastTxnType  = ref('')
const filterRiskLevel    = ref('')
const filterPhlat        = ref('')
const closedExpanded  = ref(false)

watchEffect(async () => {
  const id = store.selectedCustomerId
  if (!id) { customer.value = null; return }
  customerLoading.value = true
  try {
    customer.value = await store.fetchCustomer(id)
  } finally {
    customerLoading.value = false
  }
})

function customerRouteId(r) { return store.routeCustomerId(r) }

const analysisDate = computed(() => {
  const ts = store.timestamp
  if (!ts) return 'ไม่ทราบวันที่'
  return new Date(ts).toLocaleDateString('th-TH', { day: 'numeric', month: 'long', year: 'numeric' })
})

const totalTickets = computed(() => {
  if (!customer.value) return 0
  return customer.value.openChains + customer.value.redeemedChains + (customer.value.forfeitedChains || 0) + (customer.value.transferredChains || 0)
})

const forfeitPct = computed(() => {
  if (!customer.value || totalTickets.value === 0) return 0
  return (customer.value.forfeitedChains / totalTickets.value) * 100
})

const forfeitNormal = computed(() => {
  if (!customer.value) return 0
  return customer.value.forfeitedByPawn || 0
})

const forfeitAfterRenew = computed(() => {
  if (!customer.value) return 0
  const c = customer.value
  return (c.forfeitedByRenew || 0) + (c.forfeitedByReduce || 0) + (c.forfeitedByIncrease || 0)
})

const forfeitNormalPrincipal = computed(() => {
  if (!customer.value) return 0
  return customer.value.forfeitedPrincipalByPawn || 0
})

const forfeitAfterRenewPrincipal = computed(() => {
  if (!customer.value) return 0
  const c = customer.value
  return (c.forfeitedPrincipalByRenew || 0) + (c.forfeitedPrincipalByReduce || 0) + (c.forfeitedPrincipalByIncrease || 0)
})

const phlatCount = computed(() => {
  if (!customer.value?.openTickets) return 0
  return customer.value.openTickets.filter(
    t => t.phlatStatus === 'IN_EXTENSION_WINDOW' || t.phlatStatus === 'PAST_PHLAT_UNTIL'
  ).length
})

const phlatPrincipal = computed(() => {
  if (!customer.value?.openTickets) return 0
  return customer.value.openTickets
    .filter(t => t.phlatStatus === 'IN_EXTENSION_WINDOW' || t.phlatStatus === 'PAST_PHLAT_UNTIL')
    .reduce((sum, t) => sum + (Number(t.principal) || 0), 0)
})

// ── Wallet — placeholder รอเชื่อม API ─────────────────────────
const walletBalance  = ref(0)
const depositCount   = ref(0)
const depositTotal   = ref(0)
const withdrawCount  = ref(0)
const withdrawTotal  = ref(0)

const ticketBreakdown = computed(() => {
  if (!customer.value) return []
  const c = customer.value
  return [
    { key: 'pawn',     label: 'จำนำ (ใหม่)',  count: c.openByPawn,     principal: c.principalByPawn     },
    { key: 'renew',    label: 'ต่อดอก',       count: c.openByRenew,    principal: c.principalByRenew    },
    { key: 'reduce',   label: 'ลดต้น',        count: c.openByReduce,   principal: c.principalByReduce   },
    { key: 'increase', label: 'เพิ่มต้น',     count: c.openByIncrease, principal: c.principalByIncrease },
  ].filter(tb => tb.count > 0)
})

const closedTicketBreakdown = computed(() => {
  if (!customer.value) return []
  const c = customer.value
  return [
    { key: 'pawn',     label: 'จำนำ',    count: c.redeemedByPawn,     principal: c.redeemedPrincipalByPawn     },
    { key: 'renew',    label: 'ต่อดอก',  count: c.redeemedByRenew,    principal: c.redeemedPrincipalByRenew    },
    { key: 'reduce',   label: 'ลดต้น',   count: c.redeemedByReduce,   principal: c.redeemedPrincipalByReduce   },
    { key: 'increase', label: 'เพิ่มต้น', count: c.redeemedByIncrease, principal: c.redeemedPrincipalByIncrease },
  ].filter(tb => tb.count > 0)
})

const openTicketAssetOptions = computed(() => {
  if (!customer.value?.openTickets) return []
  return [...new Set(customer.value.openTickets.map(t => t.assetType).filter(Boolean))].sort()
})

const openTicketTxnOptions = computed(() => {
  if (!customer.value?.openTickets) return []
  return [...new Set(customer.value.openTickets.map(t => t.lastTxnType).filter(Boolean))]
})

const sortedOpenTickets = computed(() => {
  if (!customer.value?.openTickets) return []
  return [...customer.value.openTickets]
    .filter(t => !filterAssetType.value   || t.assetType       === filterAssetType.value)
    .filter(t => !filterLastTxnType.value || t.lastTxnType    === filterLastTxnType.value)
    .filter(t => !filterRiskLevel.value   || ticketRiskLevel(t) === filterRiskLevel.value)
    .filter(t => !filterPhlat.value
      || (filterPhlat.value === 'yes' && (t.phlatStatus === 'IN_EXTENSION_WINDOW' || t.phlatStatus === 'PAST_PHLAT_UNTIL'))
      || (filterPhlat.value === 'no'  && !t.phlatStatus))
    .sort((a, b) => {
      const dueDiff = a.daysUntilDue - b.daysUntilDue
      if (dueDiff !== 0) return dueDiff
      return (a.ticketNo || '').localeCompare(b.ticketNo || '', undefined, { numeric: true })
    })
})

function ticketRiskLevel(t) {
  if (t.riskLevel) return t.riskLevel === 'MEDIUM' ? 'mid' : t.riskLevel.toLowerCase()
  if (t.daysUntilDue < 0) return 'high'
  if (t.daysUntilDue <= 30) return 'mid'
  return 'low'
}
function ticketRiskLabel(t) {
  if (t.riskLevel) return t.riskLevel === 'HIGH' ? 'สูง' : t.riskLevel === 'MEDIUM' ? 'ปานกลาง' : 'ต่ำ'
  if (t.daysUntilDue < 0) return 'เกินกำหนด'
  if (t.daysUntilDue <= 30) return 'ใกล้ครบ'
  return 'ปกติ'
}
function ticketRowClass(t) {
  if (t.daysUntilDue < 0) return 'ot-row-overdue'
  if (t.daysUntilDue <= 30) return 'ot-row-near'
  return ''
}
function fmtDate(iso) {
  if (!iso) return '—'
  const d = new Date(iso)
  return d.toLocaleDateString('th-TH', { day: 'numeric', month: 'short', year: '2-digit' })
}
function fmtDays(d) {
  if (d < 0) return `เกิน ${Math.abs(d)} วัน`
  if (d === 0) return 'ครบกำหนดวันนี้'
  return `เหลือ ${d} วัน`
}
function daysClass(d) {
  if (d < 0) return 'tag-red'
  if (d <= 30) return 'tag-amber'
  return 'tag-green'
}
function txnLabel(t) {
  return { Pawn: 'จำนำ', Renew: 'ต่อดอก', Reduce: 'ลดต้น', Increase: 'เพิ่มต้น' }[t] ?? t
}

const signals = computed(() => {
  if (!customer.value) return []
  const c = customer.value
  return [
    { lbl: 'Risk Trend',    val: c.riskTrendAdj,         desc: sigDesc('risk',  c.riskTrendAdj) },
    { lbl: 'ต่อดอกช้า',    val: c.lateRenewalTrendAdj,  desc: sigDesc('late',  c.lateRenewalTrendAdj) },
    { lbl: 'Early Warning', val: c.earlyWarningAdj,      desc: sigDesc('warn',  c.earlyWarningAdj) },
    { lbl: 'Value Trend',   val: c.valueTrendAdj,        desc: sigDesc('value', c.valueTrendAdj) },
  ]
})

function fmtNum(n, dec = 0) {
  return Number(n).toLocaleString('th-TH', { minimumFractionDigits: dec, maximumFractionDigits: dec })
}

function fmtBaht(v) {
  v = Number(v)
  if (isNaN(v)) return '฿0'
  if (v >= 1000000) return '฿' + (v / 1000000).toFixed(1) + 'M'
  if (v >= 1000)    return '฿' + (v / 1000).toFixed(1) + 'K'
  return '฿' + Number(v).toLocaleString('th-TH', { maximumFractionDigits: 0 })
}

function fmtBahtFull(v) {
  v = Number(v)
  if (isNaN(v)) return '฿0'
  return '฿' + Number(v).toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

function bandCls(score)      { return score >= 67 ? 'band-high' : score >= 34 ? 'band-mid' : 'band-low' }
function bandLabelFull(score) { return score >= 67 ? 'สูง (HIGH)' : score >= 34 ? 'กลาง (MID)' : 'ต่ำ (LOW)' }
function dmiCls(dmi)          { return dmi >= 70 ? 'band-high' : dmi >= 30 ? 'band-mid' : 'band-low' }
function dmiLabel(dmi) {
  if (dmi >= 70) return 'ดี — ข้อมูลครบ'
  if (dmi >= 50) return 'พอใช้'
  if (dmi >= 30) return 'ปานกลาง'
  return 'ต่ำ — ข้อมูลน้อย'
}

function tierDesc(key) {
  const map = {
    Platinum: 'ลูกค้าชั้นเยี่ยม — ความเสี่ยงต่ำ มูลค่าสูง',
    Gold:     'ลูกค้าชั้นดี — ความเสี่ยงต่ำ มูลค่าปานกลาง',
    Silver:   'ลูกค้าปกติดี — ความเสี่ยงต่ำ มูลค่าต่ำ',
    Watch:    'ต้องติดตาม — ความเสี่ยงกลาง มูลค่าสูง',
    Preferred:'ลูกค้าทั่วไปดี — ความเสี่ยงกลาง มูลค่าปานกลาง',
    Standard: 'ลูกค้าทั่วไป — ความเสี่ยงกลาง มูลค่าต่ำ',
    Manage:   'ติดตามใกล้ชิด — ความเสี่ยงสูง มูลค่าสูง',
    Guard:    'เฝ้าระวัง — ความเสี่ยงสูง มูลค่าปานกลาง',
    Caution:  'ระวัง — ความเสี่ยงสูง มูลค่าต่ำ',
  }
  return map[key] || key
}

function sigDesc(type, v) {
  if (v === 0) return 'ปกติ'
  if (type === 'risk')  return v > 0 ? 'ดีขึ้น'        : 'แย่ลง'
  if (type === 'late')  return v > 0 ? 'ตรงเวลาขึ้น'   : 'ช้ากว่าเดิม'
  if (type === 'warn')  return v < 0 ? 'มีค้างชำระ'     : 'ปกติ'
  if (type === 'value') return v > 0 ? 'ดีขึ้น'        : 'แย่ลง'
  return ''
}

function sigCls(v) { return v > 0 ? 'sc-pos' : v < 0 ? 'sc-neg' : 'sc-neu' }

function ticketProbClass(t) {
  const level = ticketRiskLevel(t)
  if (level === 'high') return 'prob-high'
  if (level === 'mid')  return 'prob-mid'
  return 'prob-low'
}
function ticketProbBarStyle(t) {
  const level = ticketRiskLevel(t)
  const color = level === 'high' ? '#dc2626' : level === 'mid' ? '#d97706' : '#059669'
  return { width: Math.min(Number(t.forfeitProbabilityPct) || 0, 100) + '%', background: color }
}
</script>

<style scoped>
.port-root {
  --bg: #f0f2f5;
  --surface: #ffffff;
  --border: #e2e6ea;
  --text: #1a1d23;
  --muted: #6b7280;
  --primary: #1d4ed8;
  --radius: 10px;
  --shadow: 0 1px 4px rgba(0,0,0,.08), 0 4px 16px rgba(0,0,0,.06);
  --green: #059669;
  --amber: #d97706;
  --red: #dc2626;
  font-family: var(--app-font);
  line-height: 1.5;
  background: var(--bg);
  color: var(--text);
  min-height: 100vh;
}

/* ── Page header ─────────────────────────────────────────────── */
.page-hdr {
  background: linear-gradient(135deg, #05602d 0%, #05af50 100%);
  color: #fff; padding: 15px 26px;
  display: flex; align-items: center; gap: 14px; flex-wrap: wrap;
  box-shadow: 0 2px 10px rgba(0,0,0,.25);
  border-radius: var(--radius);
  text-shadow: 0 1px 4px rgba(0,0,0,.55);
}
.page-hdr-info { flex: 1; min-width: 0; }
.page-hdr-name { font-size: 1.12rem; font-weight: 700; display: flex; align-items: center; gap: 10px; flex-wrap: wrap; line-height: 1.5; }
.page-hdr-sub  { font-size: .75rem; color: rgba(255,255,255,.8); margin-top: 2px; }
.btn-hdr-ghost {
  padding: 7px 15px; border-radius: 7px; font-size: .8rem; font-weight: 600;
  background: rgba(255,255,255,.12); color: #fff; border: 1px solid rgba(255,255,255,.2);
  cursor: pointer; white-space: nowrap;
}
.btn-hdr-solid {
  padding: 7px 15px; border-radius: 7px; font-size: .8rem; font-weight: 600;
  background: #fff; color: #1d4ed8; border: none; cursor: pointer; white-space: nowrap; flex-shrink: 0;
}

/* ── Main body ───────────────────────────────────────────────── */
.port-main { width: 100%; padding: 16px 24px 36px; }
.section { margin-bottom: 16px; }
.section-lbl {
  font-size: .68rem; font-weight: 700; color: var(--muted);
  text-transform: uppercase; letter-spacing: .6px; margin-bottom: 9px;
}
.cs-card { background: var(--surface); border-radius: var(--radius); box-shadow: var(--shadow); padding: 20px 22px; display: block; border: none; }
.cs-card-title { font-size: .68rem; font-weight: 700; color: var(--muted); text-transform: uppercase; letter-spacing: .6px; margin-bottom: 14px; }
.divider { height: 1px; background: var(--border); margin: 14px 0; }

/* ── Alert banners ───────────────────────────────────────────── */
.alert-banner {
  border-radius: 9px; padding: 10px 16px;
  display: flex; align-items: flex-start; gap: 11px;
  font-size: .83rem; margin-bottom: 12px;
}
.alert-icon { font-size: 1.05rem; flex-shrink: 0; margin-top: 1px; }
.alert-red   { background: #fff5f5; border: 1px solid #fecaca; border-left: 4px solid #ef4444; }
.alert-amber { background: #fffbeb; border: 1px solid #fde68a; border-left: 4px solid #f59e0b; }
.alert-red   .alert-text { color: #7f1d1d; }
.alert-amber .alert-text { color: #78350f; }
.alert-text strong { font-weight: 700; }

/* ── Score summary ───────────────────────────────────────────── */
.score-summary {
  display: grid;
  grid-template-columns: auto 1fr 1fr 1fr;
  align-items: stretch;
}
.ss-tier {
  padding: 20px 28px;
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  border-right: 1px solid var(--border); gap: 8px;
}
.ss-tier-name { font-size: .8rem; color: var(--muted); text-align: center; max-width: 150px; margin-top: 4px; line-height: 1.4; }
.ss-score {
  padding: 16px 20px; border-right: 1px solid var(--border);
  display: flex; flex-direction: column; justify-content: center;
}
.ss-score-lbl  { font-size: .68rem; font-weight: 700; color: var(--muted); text-transform: uppercase; letter-spacing: .5px; margin-bottom: 6px; }
.ss-score-val  { font-size: 2.4rem; font-weight: 900; line-height: 1; margin-bottom: 6px; }
.ss-risk-val   { color: #d97706; }
.ss-value-val  { color: #059669; }
.ss-dmi-val    { color: #6366f1; }
.ss-bar-bg     { height: 8px; background: #f3f4f6; border-radius: 4px; overflow: hidden; margin-bottom: 5px; }
.ss-bar-fill   { height: 100%; border-radius: 4px; }
.ss-risk-fill  { background: linear-gradient(90deg,#fcd34d,#f59e0b); }
.ss-value-fill { background: linear-gradient(90deg,#6ee7b7,#10b981); }
.ss-dmi-fill   { background: linear-gradient(90deg,#a5b4fc,#6366f1); }
.ss-band       { font-size: .72rem; }

/* ── Main 2-col ──────────────────────────────────────────────── */
.main-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }

/* ── Ticket stats ────────────────────────────────────────────── */
.ticket-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
.t-stat { background: #f8faff; border-radius: 9px; padding: 16px 18px; }
.t-stat-lbl { font-size: .68rem; color: var(--muted); text-transform: uppercase; letter-spacing: .4px; margin-bottom: 4px; }
.t-stat-val { font-size: 2rem; font-weight: 900; line-height: 1; }
.t-stat-sub { font-size: .72rem; color: var(--muted); margin-top: 4px; }
.ts-open    { color: var(--primary); }
.t-stat-phlat { font-size: .7rem; color: #7c3aed; font-weight: 600; margin-top: 3px; }
.ts-overdue { color: var(--red); }
.ts-closed   { color: #475569; }
.ts-forfeit  { color: #d97706; }
.ts-ok       { color: #9ca3af; }
.ts-transfer { color: #1565c0; }
.t-stat-breakdown { display: flex; flex-direction: column; gap: 1px; margin-top: 4px; font-size: .67rem; color: #94a3b8; line-height: 1.4; }

/* ── Overdue detail box ──────────────────────────────────────── */
.overdue-box { background: #fff5f5; border: 1px solid #fecaca; border-radius: 8px; padding: 14px 16px; margin-top: 12px; }
.overdue-box-lbl { font-size: .67rem; font-weight: 700; color: #b91c1c; text-transform: uppercase; letter-spacing: .4px; margin-bottom: 10px; }
.od-row { display: flex; justify-content: space-between; align-items: center; margin-bottom: 6px; }
.od-row:last-child { margin-bottom: 0; }
.od-key   { font-size: .82rem; color: #991b1b; }
.od-val   { font-size: .9rem; font-weight: 700; color: #b91c1c; }
.od-badge { display: inline-flex; align-items: center; padding: 2px 9px; border-radius: 4px; font-size: .76rem; font-weight: 700; background: #fee2e2; color: #b91c1c; }

/* ── Forfeit detail box ──────────────────────────────────────── */
.forfeit-box { background: #fffbeb; border: 1px solid #fde68a; border-radius: 8px; padding: 14px 16px; margin-top: 12px; }
.forfeit-box-lbl { font-size: .67rem; font-weight: 700; color: #92400e; text-transform: uppercase; letter-spacing: .4px; margin-bottom: 10px; }
.transfer-box { background: #e3f2fd; border: 1px solid #90caf9; border-radius: 8px; padding: 14px 16px; margin-top: 12px; }
.transfer-box-lbl { font-size: .67rem; font-weight: 700; color: #1565c0; text-transform: uppercase; letter-spacing: .4px; margin-bottom: 10px; }
.fod-row { display: flex; justify-content: space-between; align-items: center; margin-bottom: 6px; }
.fod-row:last-child { margin-bottom: 0; }
.fod-key   { font-size: .82rem; color: #78350f; }
.fod-badge { display: inline-flex; align-items: center; padding: 2px 9px; border-radius: 4px; font-size: .76rem; font-weight: 700; background: #fef3c7; color: #92400e; }
.fod-principal { font-size: .78rem; font-weight: 600; color: #a16207; }
.fod-divider   { height: 1px; background: #fde68a; margin: 8px 0; }
.fod-sub-row { display: flex; justify-content: space-between; align-items: center; margin-bottom: 4px; padding-left: 12px; }
.fod-sub-row:last-child { margin-bottom: 0; }
.fod-sub-key { font-size: .76rem; color: #a16207; }
.fod-sub-val { font-size: .78rem; font-weight: 600; color: #a16207; }
.fod-section-sep { height: 10px; }

/* ── Principal bars ──────────────────────────────────────────── */
.p-row { display: flex; align-items: center; gap: 12px; margin-bottom: 10px; }
.p-row:last-child { margin-bottom: 0; }
.p-label     { font-size: .8rem; color: var(--muted); min-width: 120px; }
.p-bar-wrap  { flex: 1; }
.p-bar-bg    { height: 10px; background: #f3f4f6; border-radius: 5px; overflow: hidden; }
.p-bar-fill  { height: 100%; border-radius: 5px; }
.p-open-fill    { background: linear-gradient(90deg,#60a5fa,#3b82f6); }
.p-overdue-fill { background: linear-gradient(90deg,#fca5a5,#ef4444); }
.p-amount    { font-size: .85rem; font-weight: 700; min-width: 70px; text-align: right; }

/* ── Financial grid ──────────────────────────────────────────── */
.fin-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
.fin-lbl  { font-size: .68rem; color: var(--muted); text-transform: uppercase; letter-spacing: .4px; margin-bottom: 2px; }
.fin-val  { font-size: 1.1rem; font-weight: 800; }

/* ── Interest breakdown ──────────────────────────────────────── */
.interest-breakdown { display: flex; flex-direction: column; gap: 10px; }
.ib-header { display: flex; justify-content: space-between; align-items: baseline; margin-bottom: 2px; }
.ib-lbl    { font-size: .68rem; font-weight: 700; color: var(--muted); text-transform: uppercase; letter-spacing: .4px; }
.ib-total  { font-size: 1.05rem; font-weight: 800; color: var(--text); }
.ib-row     { display: flex; align-items: center; gap: 8px; }
.ib-sub-note { font-size: .67rem; color: #94a3b8; margin: -4px 0 6px 16px; }
.ib-dot    { width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0; }
.ib-dot-closed { background: #475569; }
.ib-dot-open   { background: var(--primary); }
.ib-sub-lbl { font-size: .78rem; color: var(--muted); min-width: 130px; }
.ib-bar-wrap { flex: 1; }
.ib-bar-bg   { height: 7px; background: #f3f4f6; border-radius: 4px; overflow: hidden; }
.ib-bar-fill { height: 100%; border-radius: 4px; transition: width .3s; }
.ib-closed-fill { background: linear-gradient(90deg,#94a3b8,#475569); }
.ib-open-fill   { background: linear-gradient(90deg,#93c5fd,#3b82f6); }
.ib-pct  { font-size: .72rem; color: var(--muted); min-width: 32px; text-align: right; }
.ib-amt  { font-size: .82rem; font-weight: 700; min-width: 90px; text-align: right; }

/* ── Wallet section ─────────────────────────────────────── */
.wallet-badge   { font-size: .78rem; font-weight: 700; padding: 2px 8px; border-radius: 20px; background: #eff6ff; color: #1d4ed8; }
.wallet-deposit { color: #059669; }
.wallet-withdraw{ color: #dc2626; }

/* ── Chain type breakdown (open + closed) ───────────────── */
.tb-header {
  display: flex; align-items: center; justify-content: space-between;
  margin-bottom: 14px; cursor: pointer; user-select: none;
}
.tb-header:hover .cs-card-title { color: var(--primary); }
.tb-header-static { cursor: default; }
.tb-header-static:hover .cs-card-title { color: var(--muted); }
.tb-header-left { display: flex; align-items: center; gap: 10px; }
.tb-total-badge {
  display: inline-flex; align-items: center;
  padding: 2px 9px; border-radius: 20px;
  font-size: .72rem; font-weight: 700;
  background: #f1f5f9; color: #475569;
}
.tb-total-badge-open {
  background: #dbeafe; color: #1d4ed8;
}
.tb-chevron {
  font-size: .85rem; color: var(--muted);
  transition: transform .2s ease;
  transform: rotate(-90deg);
}
.tb-chevron-open { transform: rotate(0deg); }
.tb-grid { display: flex; flex-direction: column; gap: 10px; }
.tb-grid-animated { animation: tb-fade-in .15s ease; }
@keyframes tb-fade-in { from { opacity: 0; transform: translateY(-4px); } to { opacity: 1; transform: translateY(0); } }
.tb-row  { display: flex; align-items: center; gap: 10px; }
.tb-label-wrap { display: flex; align-items: center; gap: 7px; min-width: 130px; }
.tb-dot  { width: 10px; height: 10px; border-radius: 50%; flex-shrink: 0; }
.tb-dot-pawn     { background: #3b82f6; }
.tb-dot-renew    { background: #10b981; }
.tb-dot-reduce   { background: #f59e0b; }
.tb-dot-increase { background: #8b5cf6; }
.tb-lbl  { font-size: .82rem; color: var(--text); }
.tb-bar-wrap { flex: 1; }
.tb-bar-bg   { height: 8px; background: #f3f4f6; border-radius: 4px; overflow: hidden; }
.tb-bar-fill { height: 100%; border-radius: 4px; transition: width .3s; }
.tb-fill-pawn     { background: linear-gradient(90deg,#93c5fd,#3b82f6); }
.tb-fill-renew    { background: linear-gradient(90deg,#6ee7b7,#10b981); }
.tb-fill-reduce   { background: linear-gradient(90deg,#fcd34d,#f59e0b); }
.tb-fill-increase { background: linear-gradient(90deg,#c4b5fd,#8b5cf6); }
.tb-cnt  { font-size: .8rem; font-weight: 700; min-width: 48px; text-align: right; color: var(--text); }
.tb-pct  { font-size: .72rem; color: var(--muted); min-width: 38px; text-align: right; }
.tb-amt  { font-size: .8rem; font-weight: 700; min-width: 110px; text-align: right; color: var(--muted); }

/* ── Signals ─────────────────────────────────────────────────── */
.signals-grid { display: grid; grid-template-columns: repeat(4,1fr); gap: 12px; }
.signal-card  { background: var(--surface); border-radius: var(--radius); box-shadow: var(--shadow); padding: 14px 16px; text-align: center; }
.signal-lbl   { font-size: .65rem; font-weight: 700; color: var(--muted); text-transform: uppercase; letter-spacing: .5px; margin-bottom: 6px; }
.signal-val   { font-size: 1.6rem; font-weight: 900; line-height: 1; }
.signal-desc  { font-size: .68rem; margin-top: 5px; font-weight: 600; }
.sc-pos { color: var(--green); }
.sc-neg { color: var(--red); }
.sc-neu { color: #9ca3af; }

/* ── Band labels ─────────────────────────────────────────────── */
.band-high { color: #065f46; font-weight: 700; }
.band-mid  { color: #92400e; font-weight: 700; }
.band-low  { color: #991b1b; font-weight: 700; }

/* ── CTA ─────────────────────────────────────────────────────── */
.detail-cta {
  background: linear-gradient(135deg,#3b82f6 0%,#60a5fa 100%);
  border-radius: var(--radius); padding: 20px 24px;
  display: flex; align-items: center; justify-content: space-between; gap: 16px;
  color: #fff; box-shadow: var(--shadow);
}
.cta-left h3 { font-size: .95rem; font-weight: 700; margin-bottom: 4px; color: #fff; }
.cta-left p  { font-size: .78rem; opacity: .75; color: #fff; }
.cta-btn {
  display: inline-flex; align-items: center;
  padding: 10px 22px; background: #fff; color: #1d4ed8;
  border-radius: 8px; border: none; cursor: pointer; font-weight: 700;
  font-size: .88rem; white-space: nowrap; flex-shrink: 0;
  box-shadow: 0 1px 4px rgba(0,0,0,.12);
}

/* ── Tier badges (header dark bg) ───────────────────────────── */
.tier-hdr { display: inline-flex; align-items: center; padding: 3px 11px; border-radius: 20px; font-size: .75rem; font-weight: 700; white-space: nowrap; text-shadow: none; }
.tier-hdr-Platinum { background: #ede9fe; color: #6d28d9; }
.tier-hdr-Gold     { background: #fef9c3; color: #92400e; }
.tier-hdr-Silver   { background: #f1f5f9; color: #475569; }
.tier-hdr-Watch    { background: #dbeafe; color: #1d4ed8; }
.tier-hdr-Preferred{ background: #e0f2fe; color: #075985; }
.tier-hdr-Standard { background: #eff6ff; color: #1e40af; }
.tier-hdr-Manage   { background: #fee2e2; color: #b91c1c; }
.tier-hdr-Guard    { background: #fef3c7; color: #92400e; }
.tier-hdr-Caution  { background: #fde8e8; color: #991b1b; }

/* ── Tier badges (light bg) ──────────────────────────────────── */
.tier-badge { display: inline-flex; align-items: center; padding: 3px 11px; border-radius: 20px; font-size: .82rem; font-weight: 700; white-space: nowrap; }
.tier-Platinum { background: #ede9fe; color: #6d28d9; }
.tier-Gold     { background: #fef9c3; color: #92400e; }
.tier-Silver   { background: #f1f5f9; color: #475569; }
.tier-Watch    { background: #dbeafe; color: #1d4ed8; }
.tier-Preferred{ background: #e0f2fe; color: #075985; }
.tier-Standard { background: #eff6ff; color: #1e40af; }
.tier-Manage   { background: #fee2e2; color: #b91c1c; }
.tier-Guard    { background: #fef3c7; color: #92400e; }
.tier-Caution  { background: #fde8e8; color: #991b1b; }

/* ── Open tickets forfeit risk table ─────────────────────────── */
.ot-filters { display: flex; align-items: center; gap: 8px; margin-bottom: 12px; flex-wrap: wrap; }
.ot-filter-select { font-size: .8rem; padding: 4px 10px; border: 1px solid #e2e8f0; border-radius: 6px; background: #f8fafc; color: var(--fg); cursor: pointer; outline: none; }
.ot-filter-select:focus { border-color: var(--primary); }
.ot-filter-clear { font-size: .75rem; padding: 4px 10px; border: 1px solid #e2e8f0; border-radius: 6px; background: #fff; color: var(--muted); cursor: pointer; }
.ot-filter-clear:hover { background: #f1f5f9; color: var(--fg); }
.ot-table-wrap { overflow-x: auto; overflow-y: auto; max-height: 570px; }
.ot-table { width: 100%; border-collapse: collapse; font-size: .85rem; line-height: 1.55; }
.ot-ticket { font-family: monospace; font-size: .82rem; color: #475569; white-space: nowrap; }
.asset-chip { display: inline-block; background: #eff6ff; color: #1d4ed8; border-radius: 4px; padding: 1px 7px; font-size: .75rem; font-weight: 600; white-space: nowrap; }
.stock-no   { font-family: monospace; font-size: .78rem; color: #374151; }
.ot-muted   { color: #d1d5db; font-size: .8rem; }
@media (max-width: 700px) { .col-asset, .col-stock { display: none; } }
.ot-table thead th { position: sticky; top: 0; z-index: 1;
  background: #f8faff; padding: 8px 14px; text-align: left;
  font-size: .65rem; font-weight: 700; color: var(--muted);
  text-transform: uppercase; letter-spacing: .5px;
  border-bottom: 1px solid var(--border);
}
.ot-table thead th.ot-r { text-align: right; }
.ot-table thead th.ot-c { text-align: center; }
.ot-table tbody tr { border-bottom: 1px solid #f3f4f6; transition: background .12s; }
.ot-table tbody tr:last-child { border-bottom: none; }
.ot-table tbody tr:hover { background: #f7f9ff; }
.ot-table td { padding: 11px 14px; vertical-align: middle; line-height: 1.55; }
.ot-r { text-align: right; }
.ot-c { text-align: center; }
.ot-due { font-weight: 600; color: var(--text); }
.ot-principal { font-weight: 700; color: var(--text); }
.ot-row-overdue { background: #fff5f5; }
.ot-row-near    { background: #fffbeb; }
.phlat-badge { display: inline-block; margin-left: 4px; padding: 1px 6px; border-radius: 4px; font-size: .68rem; font-weight: 700; vertical-align: middle; }
.phlat-ok   { background: #ede9fe; color: #5b21b6; }
.phlat-late { background: #fef3c7; color: #92400e; }
.ot-txn { display: inline-block; padding: 2px 8px; border-radius: 4px; font-size: .72rem; font-weight: 700; }
.ot-txn-pawn     { background: #dbeafe; color: #1d4ed8; }
.ot-txn-renew    { background: #d1fae5; color: #065f46; }
.ot-txn-reduce   { background: #fef3c7; color: #92400e; }
.ot-txn-increase { background: #ede9fe; color: #6d28d9; }
.ot-risk { display: inline-block; padding: 2px 9px; border-radius: 4px; font-size: .72rem; font-weight: 700; }
.ot-risk-high { background: #fee2e2; color: #b91c1c; }
.ot-risk-mid  { background: #fef3c7; color: #92400e; }
.ot-risk-low  { background: #d1fae5; color: #065f46; }

/* ── Forfeit probability bar ─────────────────────────────────────── */
.prob-wrap    { display: flex; align-items: center; gap: 6px; justify-content: flex-end; }
.prob-bar-bg  { width: 52px; height: 5px; background: #f3f4f6; border-radius: 3px; overflow: hidden; flex-shrink: 0; }
.prob-bar-fill{ height: 100%; border-radius: 3px; transition: width .3s; }
.prob-pct     { font-size: .8rem; font-weight: 700; min-width: 38px; text-align: right; }
.prob-high    { color: #dc2626; }
.prob-mid     { color: #d97706; }
.prob-low     { color: #059669; }

/* ── Color helpers ───────────────────────────────────────────── */
.tag-red   { color: var(--red); font-weight: 700; }
.tag-amber { color: var(--amber); font-weight: 700; }
.tag-green { color: var(--green); font-weight: 600; }

/* ── Not-found state ─────────────────────────────────────────── */
.state-wrap    { text-align: center; padding: 80px 24px; }
.nd-icon       { font-size: 3.2rem; margin-bottom: 14px; }
.state-wrap h2 { font-size: 1.2rem; margin-bottom: 8px; }
.state-wrap p  { color: var(--muted); margin-bottom: 22px; }
.btn-back {
  display: inline-block; padding: 10px 26px; background: var(--primary);
  color: #fff; border: none; border-radius: 7px; font-weight: 600; font-size: .92rem; cursor: pointer;
}

@media (max-width: 1024px) {
  .score-summary { grid-template-columns: 1fr 1fr; }
  .ss-tier { border-right: none; border-bottom: 1px solid var(--border); grid-column: 1/-1; flex-direction: row; gap: 16px; padding: 14px 18px; }
  .main-grid { grid-template-columns: 1fr; }
  .signals-grid { grid-template-columns: repeat(2,1fr); }
}

/* ── Prob info button (in column header) ─────────────────────── */
.prob-info-btn {
  display: inline-flex; align-items: center; justify-content: center;
  width: 15px; height: 15px; border-radius: 50%;
  background: #dbeafe; color: #1d4ed8; border: none;
  font-size: .6rem; font-weight: 700; cursor: pointer;
  margin-left: 5px; vertical-align: middle; line-height: 1;
  transition: background .15s, color .15s;
  flex-shrink: 0;
}
.prob-info-btn:hover { background: #1d4ed8; color: #fff; }

/* ── Forfeit Probability Modal ───────────────────────────────── */
.prob-modal-overlay {
  position: fixed; inset: 0; z-index: 9999;
  background: rgba(15,23,42,.55); backdrop-filter: blur(2px);
  display: flex; align-items: center; justify-content: center; padding: 24px;
}
.prob-modal {
  background: #fff; border-radius: 14px; width: 100%; max-width: 520px;
  box-shadow: 0 8px 40px rgba(0,0,0,.22); overflow: hidden;
}
.prob-modal-hdr {
  display: flex; align-items: center; justify-content: space-between;
  padding: 16px 20px 14px;
  background: linear-gradient(135deg,#1e3a8a 0%,#1d4ed8 100%);
}
.prob-modal-title { font-size: .95rem; font-weight: 700; color: #fff; }
.prob-modal-close {
  background: rgba(255,255,255,.18); border: none; color: #fff;
  border-radius: 6px; width: 28px; height: 28px; cursor: pointer;
  font-size: .85rem; display: flex; align-items: center; justify-content: center;
  transition: background .15s;
}
.prob-modal-close:hover { background: rgba(255,255,255,.35); }
.prob-modal-body { padding: 18px 20px 20px; }
.prob-modal-lead {
  font-size: .84rem; color: #374151; line-height: 1.6; margin: 0 0 14px;
}
.prob-modal-lead strong { color: #111827; }
.prob-modal-lead em { font-style: normal; font-weight: 700; color: #1d4ed8; }
.prob-modal-formula {
  background: #f0f9ff; border: 1px solid #bae6fd; border-radius: 8px;
  padding: 10px 14px; margin-bottom: 14px;
  display: flex; align-items: center; gap: 10px; flex-wrap: wrap;
}
.prob-formula-label { font-size: .7rem; font-weight: 700; color: #0369a1; text-transform: uppercase; letter-spacing: .4px; white-space: nowrap; }
.prob-modal-formula code {
  font-family: monospace; font-size: .83rem; font-weight: 700;
  color: #0c4a6e; background: none; padding: 0;
}
.prob-modal-factors { border: 1px solid #e5e7eb; border-radius: 9px; overflow: hidden; margin-bottom: 14px; }
.pmf-row {
  display: flex; align-items: flex-start; gap: 12px;
  padding: 11px 14px; border-bottom: 1px solid #f3f4f6;
}
.pmf-badge {
  width: 28px; height: 28px; border-radius: 7px; font-size: .7rem; font-weight: 800;
  display: flex; align-items: center; justify-content: center; flex-shrink: 0; margin-top: 1px;
}
.pmf-p1 { background: #fee2e2; color: #b91c1c; }
.pmf-p2 { background: #fef3c7; color: #92400e; }
.pmf-p3 { background: #fff7ed; color: #c2410c; }
.pmf-p4 { background: #eff6ff; color: #1d4ed8; }
.pmf-content { flex: 1; min-width: 0; }
.pmf-name { font-size: .82rem; font-weight: 700; color: #111827; margin-bottom: 3px; }
.pmf-weight { font-size: .72rem; font-weight: 600; color: #6b7280; margin-left: 6px; }
.pmf-desc { font-size: .76rem; color: #6b7280; line-height: 1.5; }
.prob-modal-levels { background: #f8faff; border: 1px solid #e2e6ea; border-radius: 8px; padding: 11px 14px; margin-bottom: 12px; }
.pml-title { font-size: .65rem; font-weight: 700; color: #6b7280; text-transform: uppercase; letter-spacing: .5px; margin-bottom: 8px; }
.pml-row { display: flex; align-items: center; gap: 8px; margin-bottom: 5px; }
.pml-row:last-child { margin-bottom: 0; }
.pml-dot { width: 10px; height: 10px; border-radius: 50%; flex-shrink: 0; }
.pml-high { background: #dc2626; }
.pml-mid  { background: #d97706; }
.pml-low  { background: #059669; }
.pml-label { font-size: .8rem; font-weight: 600; color: #374151; flex: 1; }
.pml-range { font-size: .78rem; font-weight: 700; color: #6b7280; font-family: monospace; }
.prob-modal-note {
  font-size: .72rem; color: #9ca3af; line-height: 1.5;
  margin: 0; padding-top: 2px;
}
</style>
